import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, Shield, ArrowLeft, Building2, CalendarDays, Users, IndianRupee, Gamepad2 } from "lucide-react";
import { toast } from "sonner";

export default function PlatformAdmin() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [tab, setTab] = useState("overview");
  const [stats, setStats] = useState(null);
  const [events, setEvents] = useState([]);
  const [orgs, setOrgs] = useState([]);
  const [payments, setPayments] = useState([]);
  const [users, setUsers] = useState([]);
  const [pricing, setPricing] = useState(null);

  useEffect(() => {
    api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/admin/events").then((r) => setEvents(r.data)).catch(() => {});
    api.get("/admin/organizations").then((r) => setOrgs(r.data)).catch(() => {});
    api.get("/admin/payments").then((r) => setPayments(r.data)).catch(() => {});
    api.get("/admin/users").then((r) => setUsers(r.data)).catch(() => {});
    api.get("/admin/pricing").then((r) => setPricing(r.data)).catch(() => {});
  }, []);

  const savePricing = async () => { await api.put("/admin/pricing", pricing); toast.success("Pricing updated"); };
  const resolvePayment = async (id) => { await api.post(`/admin/payments/${id}/resolve`); toast.success("Payment reconciled"); api.get("/admin/payments").then((r) => setPayments(r.data)); };
  const suspend = async (uid, val) => { await api.post(`/admin/users/${uid}/suspend`, { suspended: val }); setUsers((u) => u.map((x) => x.user_id === uid ? { ...x, suspended: val } : x)); };

  if (!stats) return <div className="min-h-screen grid place-items-center"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;

  const TABS = ["overview", "events", "organizations", "payments", "users", "pricing"];
  return (
    <div className="min-h-screen">
      <header className="glass border-b border-border sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-5 h-16 flex items-center gap-3">
          <button onClick={() => navigate("/dashboard")} className="text-muted-foreground"><ArrowLeft className="w-5 h-5" /></button>
          <div className="font-head font-black text-lg flex items-center gap-2"><Shield className="w-5 h-5 text-primary" />Platform Admin</div>
        </div>
        <div className="max-w-6xl mx-auto px-5 flex gap-1 overflow-x-auto no-scrollbar">
          {TABS.map((t) => <button key={t} onClick={() => setTab(t)} data-testid={`admin-tab-${t}`} className={`px-4 py-3 text-sm font-semibold capitalize border-b-2 whitespace-nowrap ${tab === t ? "border-primary text-primary" : "border-transparent text-muted-foreground"}`}>{t}</button>)}
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-5 py-8">
        {tab === "overview" && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[["Organizations", stats.organizations, Building2], ["Events", stats.events, CalendarDays], ["Active events", stats.active_events, Gamepad2], ["Participants", stats.participants, Users], ["Revenue", `₹${stats.revenue}`, IndianRupee], ["Payments", stats.payments_count, IndianRupee]].map(([l, v, I], i) => (
              <div key={i} className="brut p-5"><I className="w-5 h-5 text-primary mb-2" /><div className="font-mono-num font-extrabold text-3xl">{v}</div><div className="text-xs text-muted-foreground uppercase">{l}</div></div>
            ))}
            <div className="brut p-5 col-span-2 md:col-span-4">
              <h3 className="font-head font-bold mb-3">Popular games</h3>
              <div className="flex flex-wrap gap-2">{stats.popular_games.map((g) => <span key={g.game_type} className="px-3 py-1.5 rounded-full bg-secondary text-sm font-mono-num">{g.name} · {g.count}</span>)}{stats.popular_games.length === 0 && <span className="text-muted-foreground text-sm">No plays yet.</span>}</div>
            </div>
          </div>
        )}

        {tab === "events" && <Table cols={["Name", "Type", "Owner", "Players", "Status"]} rows={events.map((e) => [e.name, e.event_type, e.owner_email, e.player_count, e.status])} />}
        {tab === "organizations" && <Table cols={["Org", "Owner", "Events"]} rows={orgs.map((o) => [o.name || "—", o.owner_email, o.event_count])} />}
        {tab === "payments" && (
          <div className="brut overflow-x-auto"><table className="w-full text-sm min-w-[780px]">
            <thead className="bg-secondary"><tr>{["Order ID", "Event", "Package", "Participants", "Amount", "Status", "Date", ""].map((h) => <th key={h} className="text-left p-3">{h}</th>)}</tr></thead>
            <tbody>
              {payments.map((p) => (
                <tr key={p.payment_id} className="border-t border-border" data-testid={`admin-pay-${p.payment_id}`}>
                  <td className="p-3 font-mono-num text-xs">{p.order_id || p.invoice_no}</td>
                  <td className="p-3">{p.event_name}</td>
                  <td className="p-3">{p.package || "—"}</td>
                  <td className="p-3 font-mono-num">{p.participant_count ?? "—"}</td>
                  <td className="p-3 font-mono-num">₹{p.amount}</td>
                  <td className="p-3">{p.status === "paid" ? <span className="text-neon-green font-semibold">paid</span> : p.status === "paid_unreconciled" ? <span className="text-primary font-semibold">unreconciled</span> : <span className="text-muted-foreground">{p.status}</span>}</td>
                  <td className="p-3">{new Date(p.created_at).toLocaleDateString()}</td>
                  <td className="p-3">{p.status === "paid_unreconciled" && <Button size="sm" className="rounded-full" onClick={() => resolvePayment(p.payment_id)} data-testid={`resolve-${p.payment_id}`}>Resolve</Button>}</td>
                </tr>
              ))}
              {payments.length === 0 && <tr><td colSpan={8} className="p-6 text-center text-muted-foreground">No payments.</td></tr>}
            </tbody>
          </table></div>
        )}

        {tab === "users" && (
          <div className="brut overflow-x-auto"><table className="w-full text-sm min-w-[560px]">
            <thead className="bg-secondary"><tr>{["Name", "Email", "Role", "Status", ""].map((h) => <th key={h} className="text-left p-3">{h}</th>)}</tr></thead>
            <tbody>{users.map((u) => (
              <tr key={u.user_id} className="border-t border-border">
                <td className="p-3 font-semibold">{u.name}</td><td className="p-3 text-muted-foreground">{u.email}</td><td className="p-3">{u.role}</td>
                <td className="p-3">{u.suspended ? <span className="text-primary">Suspended</span> : <span className="text-neon-green">Active</span>}</td>
                <td className="p-3">{u.role !== "admin" && <Button size="sm" variant={u.suspended ? "secondary" : "ghost"} className="rounded-full" onClick={() => suspend(u.user_id, !u.suspended)} data-testid={`suspend-${u.user_id}`}>{u.suspended ? "Reinstate" : "Suspend"}</Button>}</td>
              </tr>
            ))}</tbody>
          </table></div>
        )}

        {tab === "pricing" && pricing && (
          <div className="brut p-6 max-w-md">
            <h2 className="font-head font-bold text-lg mb-4">Participant pricing</h2>
            <div className="space-y-3">
              <div><Label className="mb-1 block">Free participant allowance</Label><Input type="number" value={pricing.free_allowance} onChange={(e) => setPricing({ ...pricing, free_allowance: Number(e.target.value) })} data-testid="price-free" /></div>
              <div><Label className="mb-1 block">Block size</Label><Input type="number" value={pricing.block_size} onChange={(e) => setPricing({ ...pricing, block_size: Number(e.target.value) })} data-testid="price-block" /></div>
              <div><Label className="mb-1 block">Block price (₹)</Label><Input type="number" value={pricing.block_price} onChange={(e) => setPricing({ ...pricing, block_price: Number(e.target.value) })} data-testid="price-amount" /></div>
              <Button className="rounded-full font-bold" onClick={savePricing} data-testid="save-pricing">Save pricing</Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function Table({ cols, rows }) {
  return (
    <div className="brut overflow-x-auto"><table className="w-full text-sm min-w-[560px]">
      <thead className="bg-secondary"><tr>{cols.map((c) => <th key={c} className="text-left p-3">{c}</th>)}</tr></thead>
      <tbody>{rows.map((r, i) => <tr key={i} className="border-t border-border">{r.map((c, j) => <td key={j} className="p-3">{c}</td>)}</tr>)}
        {rows.length === 0 && <tr><td colSpan={cols.length} className="p-6 text-center text-muted-foreground">No data.</td></tr>}
      </tbody>
    </table></div>
  );
}
